import React, { ReactNode } from 'react';
import { Link } from '@inertiajs/react';

interface Props {
  children: ReactNode;
}

const CheckoutLayout: React.FC<Props> = ({ children }) => {
  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-indigo-950 to-slate-900 relative">
      {/* Animated moving starfield background dots */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div 
          className="absolute w-1 h-1 bg-white rounded-full opacity-60"
          style={{ 
            animation: 'float1 20s linear infinite',
            animationDelay: '0s'
          }}
        ></div>
        <div 
          className="absolute w-0.5 h-0.5 bg-white rounded-full opacity-40"
          style={{ 
            animation: 'float2 25s linear infinite',
            animationDelay: '2s'
          }}
        ></div>
        <div 
          className="absolute w-1 h-1 bg-white rounded-full opacity-50"
          style={{ 
            animation: 'float3 30s linear infinite',
            animationDelay: '4s'
          }}
        ></div>
        <div 
          className="absolute w-0.5 h-0.5 bg-white rounded-full opacity-30"
          style={{ 
            animation: 'float4 18s linear infinite',
            animationDelay: '1s'
          }}
        ></div>
        <div 
          className="absolute w-1 h-1 bg-white rounded-full opacity-45"
          style={{ 
            animation: 'float5 22s linear infinite',
            animationDelay: '3s'
          }}
        ></div>
        <div 
          className="absolute w-0.5 h-0.5 bg-white rounded-full opacity-35"
          style={{ 
            animation: 'float6 28s linear infinite',
            animationDelay: '5s'
          }}
        ></div>
        <div 
          className="absolute w-1 h-1 bg-white rounded-full opacity-55"
          style={{ 
            animation: 'float7 24s linear infinite',
            animationDelay: '1.5s'
          }}
        ></div>
        <div 
          className="absolute w-0.5 h-0.5 bg-white rounded-full opacity-40"
          style={{ 
            animation: 'float8 26s linear infinite',
            animationDelay: '3.5s'
          }}
        ></div>
        <div 
          className="absolute w-1 h-1 bg-white rounded-full opacity-30"
          style={{ 
            animation: 'float9 32s linear infinite',
            animationDelay: '0.5s'
          }}
        ></div>
        <div 
          className="absolute w-0.5 h-0.5 bg-white rounded-full opacity-50"
          style={{ 
            animation: 'float10 19s linear infinite',
            animationDelay: '2.5s'
          }}
        ></div>
      </div>

      <style>{`
        @keyframes float1 {
          0% { transform: translate(10vw, 20vh); }
          25% { transform: translate(80vw, 10vh); }
          50% { transform: translate(90vw, 60vh); }
          75% { transform: translate(20vw, 80vh); }
          100% { transform: translate(10vw, 20vh); }
        }
        @keyframes float2 {
          0% { transform: translate(25vw, 35vh); }
          25% { transform: translate(5vw, 70vh); }
          50% { transform: translate(75vw, 85vh); }
          75% { transform: translate(95vw, 15vh); }
          100% { transform: translate(25vw, 35vh); }
        }
        @keyframes float3 {
          0% { transform: translate(45vw, 15vh); }
          25% { transform: translate(15vw, 45vh); }
          50% { transform: translate(85vw, 25vh); }
          75% { transform: translate(55vw, 75vh); }
          100% { transform: translate(45vw, 15vh); }
        }
        @keyframes float4 {
          0% { transform: translate(15vw, 60vh); }
          25% { transform: translate(70vw, 30vh); }
          50% { transform: translate(30vw, 90vh); }
          75% { transform: translate(85vw, 50vh); }
          100% { transform: translate(15vw, 60vh); }
        }
        @keyframes float5 {
          0% { transform: translate(60vw, 45vh); }
          25% { transform: translate(40vw, 5vh); }
          50% { transform: translate(10vw, 75vh); }
          75% { transform: translate(80vw, 40vh); }
          100% { transform: translate(60vw, 45vh); }
        }
        @keyframes float6 {
          0% { transform: translate(80vw, 25vh); }
          25% { transform: translate(35vw, 80vh); }
          50% { transform: translate(5vw, 35vh); }
          75% { transform: translate(90vw, 65vh); }
          100% { transform: translate(80vw, 25vh); }
        }
        @keyframes float7 {
          0% { transform: translate(35vw, 70vh); }
          25% { transform: translate(90vw, 20vh); }
          50% { transform: translate(20vw, 55vh); }
          75% { transform: translate(65vw, 85vh); }
          100% { transform: translate(35vw, 70vh); }
        }
        @keyframes float8 {
          0% { transform: translate(70vw, 80vh); }
          25% { transform: translate(25vw, 15vh); }
          50% { transform: translate(95vw, 70vh); }
          75% { transform: translate(45vw, 30vh); }
          100% { transform: translate(70vw, 80vh); }
        }
        @keyframes float9 {
          0% { transform: translate(75vw, 5vh); }
          25% { transform: translate(30vw, 60vh); }
          50% { transform: translate(85vw, 90vh); }
          75% { transform: translate(10vw, 25vh); }
          100% { transform: translate(75vw, 5vh); }
        }
        @keyframes float10 {
          0% { transform: translate(20vw, 90vh); }
          25% { transform: translate(85vw, 35vh); }
          50% { transform: translate(40vw, 10vh); }
          75% { transform: translate(75vw, 75vh); }
          100% { transform: translate(20vw, 90vh); }
        }
      `}</style>
      {/* Navigation Bar - Fixed glass pill at top */}
      <nav className="fixed top-3 inset-x-0 z-50 mx-auto w-[calc(100%-2rem)] max-w-5xl rounded-2xl border border-white/10 bg-slate-950/50 shadow-lg shadow-black/30 backdrop-blur-md">
        <div className="w-full px-6 lg:px-8">
          <div className="flex items-center justify-center h-14">
            {/* Logo - Centered */}
            <img src="/landing/images/sz-orb-logo-white.png" alt="StationZero" className="h-6" />
          </div>
        </div>
      </nav>

      {/* Main Content */}
      <main className="relative flex-1 z-10">
        {children}
      </main>

      {/* Footer */}
      <footer className="relative bg-black/20 border-t border-white/10 mt-auto z-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="flex items-center gap-3 mb-4 md:mb-0">
              <img src="/landing/images/sz-orb-logo-white.png" alt="" aria-hidden="true" className="h-5 opacity-50" />
              <span className="text-white/50 text-sm tracking-tight">
                © {new Date().getFullYear()} Todos os direitos reservados.
              </span>
            </div>
            
            <div className="flex space-x-6">
              <Link href="/privacy" className="text-white/50 hover:text-white text-sm transition-colors tracking-tight">
                Política de Privacidade
              </Link>
              <Link href="#termos" className="text-white/50 hover:text-white text-sm transition-colors tracking-tight">
                Termos de Uso
              </Link>
              <Link href="#suporte" className="text-white/50 hover:text-white text-sm transition-colors tracking-tight">
                Suporte
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CheckoutLayout;